import { useTheme } from '@emotion/react';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import { ChangeEvent, useEffect, useMemo, useState } from 'react';
import { LineSeparator } from '../../../common/TwoButtonModal/TwoButtonModal.styles';
import Button from '../../../components/Button/Button';
import { Variant } from '../../../components/Button/Button.types';
import Dropdown from '../../../components/Dropdown/Dropdown';
import Input from '../../../components/Input/Input';
import Modal from '../../../components/Modal/Modal';
import { showToast } from '../../../components/Toast/Toast';
import { SPEC_STATUS } from '../../../constants/ApiEnums';
import { useUpdateMenuItemRecipe, useUpdateMenuItemSubRecipe } from '../../../services/menu/menu.service';
import { useSpecsInRecipe, useSpecsInSubrecipe } from '../../../services/spec/spec.service';
import { useFetchErrorToast } from '../../../utils/useFetchErrorToasts/useFetchErrorToast';
import { useGenericErrorMessage } from '../../../utils/useGenericErrorMessage/useGenericErrorMessage';
import {
  CellTextWrapper,
  DropdownWrapper,
  EditCell,
  IconButton,
  InputWrapper,
  StyledButtons,
  StyledInputs,
  StyledMessage,
  WarningWrapper,
} from './EditMenuItemSpecModal.styles';
import { EditMenuItemSpecProps } from './EditMenuItemSpecModal.types';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { Spec } from '../../../services/spec/spec.types';

export const EditMenuItemSpecModal = (props: EditMenuItemSpecProps) => {
  const theme = useTheme();
  const [menuItemSpecNo, setMenuItemSpecNo] = useState<number | undefined>(undefined);
  const [initialSpecNo] = useState<number>(props.data.specNo);
  const [modalOpen, setModalOpen] = useState<boolean>(false);

  const { data: specsInRecipe, isError: specsInRecipeIsError } = useSpecsInRecipe(
    {
      classNo: props.data.classNo,
      baseNo: props.data.baseNo,
      sizeNo: props.data.sizeNo,
      productNo: props.data.prodNo,
      statusNo: SPEC_STATUS.ACTIVE,
    },
    props.isRecipes
  );

  const { data: specsInSubrecipe, isError: specsInSubrecipesIsError } = useSpecsInSubrecipe(
    {
      classNo: props.data.classNo,
      baseNo: props.data.baseNo,
      sizeNo: props.data.sizeNo,
      productNo: props.data.prodNo,
      optionNo: props.data.optionNo,
      statusNo: SPEC_STATUS.ACTIVE,
    },
    !props.isRecipes && props.data.optionNo !== null
  );

  const {
    mutate: updateMenuItemRecipe,
    isError: updateMenuItemRecipeIsError,
    error: updateMenuItemRecipeError,
    isLoading: updateMenuItemRecipeIsLoading,
  } = useUpdateMenuItemRecipe(props.menuNo, props.data.menuItemId);

  const {
    mutate: updateMenuItemSubRecipe,
    isError: updateMenuItemSubRecipeIsError,
    error: updateMenuItemSubRecipeError,
    isLoading: updateMenuItemSubRecipeIsLoading,
  } = useUpdateMenuItemSubRecipe(props.menuNo, props.data.menuItemId);

  useFetchErrorToast([
    { isError: specsInRecipeIsError, id: 'useSpecsInRecipe', label: 'Error fetching specs.' },
    { isError: specsInSubrecipesIsError, id: 'useSpecsInRecipe', label: 'Error fetching specs.' },
  ]);

  useGenericErrorMessage([
    { isError: updateMenuItemRecipeIsError, error: updateMenuItemRecipeError },
    { isError: updateMenuItemSubRecipeIsError, error: updateMenuItemSubRecipeError },
  ]);

  // Find the subSpecNo associated with the selected specNo
  const selectedSpec = specsInSubrecipe?.data?.find((spec) => {
    return spec.specNo === menuItemSpecNo;
  });

  const findInitialSpecOnSpecList = (spec: Spec) => spec.specNo === initialSpecNo;

  useEffect(() => {
    if (props.isRecipes) {
      const spec = specsInRecipe?.data?.find(findInitialSpecOnSpecList);
      setMenuItemSpecNo(spec?.specNo ?? specsInRecipe?.data?.at(0)?.specNo);
    } else if (!props.isRecipes) {
      const spec = specsInSubrecipe?.data?.find(findInitialSpecOnSpecList);
      setMenuItemSpecNo(spec?.specNo ?? specsInSubrecipe?.data?.at(0)?.specNo);
    }
  }, [modalOpen]);

  const closeModal = () => setModalOpen(false);
  const openModal = () => {
    setModalOpen(true);
  }

  const specsInRecipeOptions = useMemo(() => {
    if (specsInRecipe?.data) {
      return specsInRecipe?.data?.map((item: any) => {
        return { value: item.specNo, label: `${item.specNo} - ${item.description}` };
      });
    } else {
      return [];
    }
  }, [specsInRecipe]);

  const specsInSubrecipeOptions = useMemo(() => {
    if (specsInSubrecipe?.data) {
      return specsInSubrecipe?.data?.map((item: any) => {
        return { value: item.specNo, label: `${item.specNo} - ${item.description}` };
      });
    } else {
      return [];
    }
  }, [specsInSubrecipe]);

  const handleSelect = (event: ChangeEvent<HTMLSelectElement>) => {
    setMenuItemSpecNo(Number(event.target.value));
  };

  const handleRecipeSpecSave = () => {
    updateMenuItemRecipe(menuItemSpecNo, {
      onSuccess: () => {
        closeModal();
        showToast({
          label: 'Menu spec has been updated successfully.',
          id: 'menuRecipeSpecUpdateSuccess',
        });
        props.menuItemRecipeRefetch();
      },
    });
  };

  const handleSubrecipeSpecSave = () => {
    updateMenuItemSubRecipe(
      {
        specNo: menuItemSpecNo,
        subSpecNo: selectedSpec?.subSpecNo,
      },
      {
        onSuccess: () => {
          closeModal();
          showToast({
            label: 'Menu spec has been updated successfully.',
            id: 'menuSubrecipeSpecUpdateSuccess',
          });
          props.menuItemSubrecipeRefetch();
        },
      }
    );
  };

  const recipeWarningWrapper = (
    <WarningWrapper>
      <ErrorOutlineIcon style={{ marginRight: '.5rem' }} />
      {`${props.value}`}
    </WarningWrapper>
  );

  return (
    <>
      <EditCell>
        {props.isChecked ? (
          recipeWarningWrapper
        ) : props.isRecipes && props.isChecked ? (
          recipeWarningWrapper
        ) : (
          <CellTextWrapper>
            {props.value && props.value.startsWith(`${props.data.specNo} - `) 
              ? props.value 
              : `${props.data.specNo} - ${props.value}`}
          </CellTextWrapper>
        )}
        {props.notPending ? (
          <></>
        ) : (
          <IconButton onClick={openModal} aria-label='edit' data-testid='EditPencilIcon'>
            <EditOutlinedIcon htmlColor={theme.colors.blue} />
          </IconButton>
        )}
      </EditCell>
      <Modal id='changeSpecModal' open={modalOpen} onClose={closeModal} title='Change Spec'>
        {props.isRecipes ? (
          <div data-testid='ChangeRecipeSpecModal'>
            <StyledMessage>Reassign the spec for the following menu item</StyledMessage>
            <StyledInputs>
              <InputWrapper>
                <Input id='class' label='Class' value={`${props.data.classNo} - ${props.data.classDesc}`} disabled />
                <Input id='base' label='Base' value={`${props.data.baseNo} - ${props.data.baseDesc}`} disabled />
              </InputWrapper>
              <InputWrapper>
                <Input id='size' label='Size' value={`${props.data.sizeNo} - ${props.data.sizeDesc}`} disabled />
                <Input id='prod' label='Prod' value={`${props.data.prodNo} - ${props.data.prodDesc}`} disabled />
              </InputWrapper>
            </StyledInputs>
            <LineSeparator />
            <DropdownWrapper>
              <Dropdown
                id='spec'
                label='Spec'
                dataTestId='selectOptionSpecsModal'
                options={specsInRecipeOptions}
                value={props.data.specNo}
                onChange={handleSelect}
              />
            </DropdownWrapper>
            <StyledButtons>
              <Button variant={Variant.Secondary} label='Cancel' onClick={closeModal} />
              <Button
                label='Save Change'
                testId='saveChangeSpecModalBtn'
                onClick={handleRecipeSpecSave}
                disabled={menuItemSpecNo === props.data.specNo  || specsInRecipeOptions.length === 0}
                isLoading={updateMenuItemRecipeIsLoading}
              />
            </StyledButtons>
          </div>
        ) : (
          <div data-testid='ChangeSubecipeSpecModal'>
            <StyledMessage>Reassign the spec for the following menu item</StyledMessage>
            <StyledInputs>
              <InputWrapper>
                <Input id='classNo' label='Class' value={`${props.data.classNo} - ${props.data.classDesc}`} disabled />
                <Input id='baseNo' label='Base' value={`${props.data.baseNo} - ${props.data.baseDesc}`} disabled />
              </InputWrapper>
              <InputWrapper>
                <Input id='sizeNo' label='Size' value={`${props.data.sizeNo} - ${props.data.sizeDesc}`} disabled />
                <Input id='prodNo' label='Prod' value={`${props.data.prodNo} - ${props.data.prodDesc}`} disabled />
              </InputWrapper>
              <InputWrapper>
                <Input
                  id='optionNo'
                  label='Option'
                  value={`${props.data.optionNo} - ${props.data.optionDesc}`}
                  disabled
                />
              </InputWrapper>
            </StyledInputs>
            <LineSeparator />
            <DropdownWrapper>
              <Dropdown
                id='specNo'
                label='Spec'
                options={specsInSubrecipeOptions}
                value={props.data.specNo}
                onChange={handleSelect}
                dataTestId='selectOptionSubSpecsModal'
              />
            </DropdownWrapper>
            <StyledButtons>
              <Button variant={Variant.Secondary} label='Cancel' onClick={closeModal} />
              <Button
                label='Save Change'
                onClick={handleSubrecipeSpecSave}
                disabled={menuItemSpecNo === props.data.specNo || specsInSubrecipeOptions.length === 0}
                isLoading={updateMenuItemSubRecipeIsLoading}
                testId='saveChangeSubSpecModalBtn'
              />
            </StyledButtons>
          </div>
        )}
      </Modal>
    </>
  );
};
